import { useState } from "react";
import Layout, { Section, PageHero } from "@/components/layout/Layout";
import { Reveal } from "@/components/common/Reveal";
import { Node, Path, Flow, Field, Spark } from "@/components/primitives/Primitives";

function VDLPlayground() {
    const [scale, setScale] = useState(1);
    const [primitive, setPrimitive] = useState("node");
    const [dark, setDark] = useState(false);
    const size = Math.round(140 * scale);
    const Prim = { node: <Node size={size} />, path: <Path className={`w-full`} />, flow: <Flow className="w-full" />, field: <Field className="w-full h-full" />, spark: <Spark className="w-full h-full" /> }[primitive];
    return (
        <div className="mt-12 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4 space-y-8">
                <div>
                    <p className="label mb-3">Primitive</p>
                    <div className="flex flex-wrap gap-2">
                        {["node","path","flow","field","spark"].map((p) => (
                            <button key={p} onClick={() => setPrimitive(p)}
                                data-testid={`playground-${p}`}
                                className={`px-4 py-2 rounded-full text-xs border transition-colors capitalize ${primitive === p ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground/40"}`}>
                                {p}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <p className="label mb-3">Scale · {scale.toFixed(2)}×</p>
                    <input type="range" min="0.5" max="2" step="0.05" value={scale}
                           data-testid="playground-scale"
                           onChange={(e) => setScale(parseFloat(e.target.value))} className="w-full accent-foreground" />
                </div>
                <div>
                    <p className="label mb-3">Canvas</p>
                    <button onClick={() => setDark(!dark)} data-testid="playground-invert"
                            className={`px-4 py-2 rounded-full text-xs border transition-colors ${dark ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground/40"}`}>
                        {dark ? "Dark" : "Light"}
                    </button>
                </div>
            </div>
            <div className={`lg:col-span-8 rounded-3xl border border-border aspect-video flex items-center justify-center p-16 transition-colors ${dark ? "bg-foreground text-background" : "bg-muted/30 text-foreground"}`}>
                <div className="w-full h-full flex items-center justify-center opacity-90">
                    {Prim}
                </div>
            </div>
        </div>
    );
}

const chapters = [
    { t: "Constitution", d: "The commitments we do not break. What we will and will not build." },
    { t: "Manifesto", d: "A short document about calm intelligence, written in plain English." },
    { t: "Brand DNA", d: "How Ventara sounds, looks, and behaves — inside and outside." },
    { t: "Visual Philosophy", d: "Whitespace, rhythm, editorial hierarchy, elegant motion." },
    { t: "Components", d: "The primitives assembled into a working system." },
    { t: "Atlas", d: "A living map of every pattern, token, and decision." },
    { t: "Playground", d: "A sandbox for testing primitives together." },
];

export default function VDL() {
    const primitives = [
        { name: "Node", meaning: "Potential", note: "A single point held open. The starting place of every decision.", C: Node },
        { name: "Path", meaning: "Connection", note: "A bezier that draws itself. What one node owes to another.", C: Path },
        { name: "Flow", meaning: "Transformation", note: "Parallel curves moving calmly. The pattern of change.", C: Flow },
        { name: "Field", meaning: "Clarity", note: "A soft mask over a subtle grid. Structure without imposition.", C: Field },
        { name: "Spark", meaning: "Convergence", note: "A point of light that expands, then rests. The moment things arrive.", C: Spark },
    ];
    return (
        <Layout title="VDL — Ventara Design Language" description="Constitution, manifesto, brand DNA, and the five primitives: Node, Path, Flow, Field, Spark.">
            <PageHero i18nKey="vdl"
                eyebrow="Ventara Design Language"
                title={<>A grammar for<br />calm intelligence.</>}
                lead="The Ventara Design Language, or VDL, is the shared spine of everything we make. Five primitives; seven chapters; one commitment — that intelligence should be legible, human, and quiet."
            />

            <Section className="pb-32">
                <p className="label">The five primitives</p>
                <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-border">
                    {primitives.map((p) => (
                        <div key={p.name} className="bg-background p-8 flex flex-col items-start" data-testid={`vdl-primitive-${p.name.toLowerCase()}`}>
                            <div className="h-32 w-full flex items-center justify-center text-foreground/80">
                                <p.C size={100} variant="brand" />
                            </div>
                            <p className="label mt-6">{p.meaning}</p>
                            <h3 className="font-serif text-3xl mt-2 tracking-tight">{p.name}</h3>
                            <p className="prose-body text-sm mt-3">{p.note}</p>
                        </div>
                    ))}
                </div>
            </Section>

            <Section className="py-24 border-t border-border">
                <p className="label">Seven chapters</p>
                <h2 className="font-serif font-light text-4xl sm:text-6xl mt-6 max-w-3xl tracking-tight leading-[1.05]">
                    A design language reads like a book — because it is one.
                </h2>
                <div className="mt-16 border-t border-border">
                    {chapters.map((c, i) => (
                        <Reveal key={c.t}>
                            <div className="grid lg:grid-cols-12 gap-8 py-8 border-b border-border" data-testid={`vdl-chapter-${i}`}>
                                <div className="lg:col-span-2 label pt-2">Chapter 0{i + 1}</div>
                                <div className="lg:col-span-4">
                                    <h3 className="font-serif text-3xl tracking-tight">{c.t}</h3>
                                </div>
                                <div className="lg:col-span-6 prose-body">{c.d}</div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </Section>

            <Section className="py-24 border-t border-border" data-testid="vdl-playground">
                <p className="label">Playground</p>
                <h2 className="font-serif font-light text-4xl sm:text-5xl mt-6 tracking-tight">Try the primitives.</h2>
                <p className="prose-body mt-6 max-w-lg">Adjust the scale. Watch the grammar hold.</p>
                <VDLPlayground />
            </Section>

            <Section className="py-24 border-t border-border">
                <div className="grid lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-6">
                        <p className="label">Manifesto</p>
                        <blockquote className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight mt-6">
                            "We are building the calm layer of the next decade — where intelligence recedes into the environment and the human moves to the foreground."
                        </blockquote>
                        <p className="prose-body mt-8">— Ventara, first paragraph.</p>
                    </div>
                    <div className="lg:col-span-6 text-foreground/70">
                        <div className="grid grid-cols-2 gap-8">
                            <Node size={140} variant="brand" />
                            <Path className="w-full h-32" variant="brand" />
                            <Flow className="w-full h-32" variant="brand" />
                            <Spark className="w-32 h-32" variant="brand" />
                        </div>
                    </div>
                </div>
            </Section>
        </Layout>
    );
}
